import { useState } from 'react';
import { hydrateFilterSnapshot, isFilterSnapshotDirty } from '../../lib/filtering/filterSnapshot';
import { extractOrderBy, isOrderByEqual } from '../../lib/filtering/orderBySnapshot';
import type { DataFilters } from '../useDataFilters';

export type SavedViewLike = { uuid: string; filters: unknown };

// gloss
export const useSavedViewSelection = <TView extends SavedViewLike>(
  views: TView[],
  dataFilters: Pick<DataFilters, 'filters' | 'hydrate' | 'orderBy' | 'setOrderBy'>,
) => {
  const [activeViewUuid, setActiveViewUuid] = useState<string | null>(null);
  const [lastAppliedView, setLastAppliedView] = useState<TView | null>(null);
  const activeView =
    views.find((view) => view.uuid === activeViewUuid) ??
    (lastAppliedView?.uuid === activeViewUuid ? lastAppliedView : null);

  const applyView = (view: TView) => {
    dataFilters.hydrate(hydrateFilterSnapshot(view.filters, {}));
    dataFilters.setOrderBy(extractOrderBy(view.filters));
    setActiveViewUuid(view.uuid);
    setLastAppliedView(view);
  };

  const selectView = (uuid: string) => {
    const view = views.find((v) => v.uuid === uuid);
    if (view) applyView(view);
  };

  const clearSelection = () => {
    setActiveViewUuid(null);
    setLastAppliedView(null);
  };

  const restoreSelection = (uuid: string) => {
    setActiveViewUuid(uuid);
  };

  const isDirty =
    isFilterSnapshotDirty(dataFilters.filters, activeView ? hydrateFilterSnapshot(activeView.filters, {}) : {}) ||
    !isOrderByEqual(dataFilters.orderBy, activeView ? extractOrderBy(activeView.filters) : []);

  return { activeViewUuid, activeView, selectView, applyView, clearSelection, restoreSelection, isDirty };
};
